import React, { useRef, useState } from "react";
import { RiExpandUpDownLine } from "react-icons/ri";
import { LinearProgress } from "@mui/material";
import useAuth from "../../context/AuthProvider";
import { ReconciliationTransactionReportModel_Resp, ReconciliationTransactionReportModel } from "../../models/TaxTransactionReportModel";
import { DataGridPro, GridColDef, GridRowId, GridSelectionModel, GridSortModel } from "@mui/x-data-grid-pro";
import formatPrice from "../../helper/currencyFormatter";
import { getRowsPerPageOptions } from "../AddEdit/CustomFunctions";

interface Iprops {
    ReportModuleCL: ReconciliationTransactionReportModel_Resp;
    setSelectedRows: React.Dispatch<React.SetStateAction<ReconciliationTransactionReportModel[] | null>>
    selectedRows: ReconciliationTransactionReportModel[] | null
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    setPageSize: React.Dispatch<React.SetStateAction<number>>
    _pageSize: number
    filterModel: { items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }
    setFilterModel: React.Dispatch<React.SetStateAction<{ items: { columnField: string, value: string, operatorValue: string }[], logicOperator?: 'and' | 'or' }>>
    setSortModel: React.Dispatch<React.SetStateAction<{ field: string, sort: string }[]>>
}

export const ReconciliationList: React.FC<Iprops> = ({
    ReportModuleCL,
    setSelectedRows,
    selectedRows,
    setCurrentPage,
    setPageSize,
    _pageSize,
    filterModel,
    setFilterModel,
    setSortModel
}) => {
    const { user } = useAuth();
    const [loading, setloading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const columns: GridColDef[] = [
        {
            field: "client", headerName: "Client", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },
        {
            field: "invoiceNumber", headerName: "InvoiceNumber", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },

        {
            field: "insuredName", headerName: "InsuredName", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },
        {
            field: "policyNumber", headerName: "PolicyNumber", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },

        {
            field: "homeState", headerName: "HomeState", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 2,
        },

        {
            field: "taxType", headerName: "TaxType", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },


        {
            field: "transactionType", headerName: "TransactionType", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },
        {
            field: "invDate",
            headerName: "InvoiceDate",
            type: "date",
            headerAlign: "center",
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            valueGetter: (params) => (
                params.row.invDate ? new Date(params.row.invDate) : null
            ),
            renderCell: (params) => {
                if (!params.value) return ""; // Return an empty string if no date value is present
        
                const date = new Date(params.value);
        
                // Check for the "1900-01-01" placeholder date or other invalid dates
                if (date.getFullYear() === 1900 && date.getMonth() === 0 && date.getDate() === 1) {
                    return ""; // Return empty if it matches the 1900 placeholder
                }
        
                // Format the date as MM/DD/YYYY
                const formattedDate = date.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric"
                });
        
                return formattedDate;
            },
            align: "center",
            flex: 3,
        },
        {
            field: 'policyEffDate',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            type: 'date',
            headerName: 'PolicyEffectiveDate',
            flex: 4,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (params) => (
                params.row.policyEffDate ? new Date(params.row.policyEffDate) : null
            ),
            renderCell: (params) => {
                if (!params.value) return ""; // If there's no value, return an empty string
        
                const date = new Date(params.value);
        
                // Check if the date is the placeholder "1900-01-01"
                if (date.getFullYear() === 1900 && date.getMonth() === 0 && date.getDate() === 1) {
                    return ""; // Return an empty string for the placeholder date
                }
        
                // Format the date as MM/DD/YYYY
                const formattedDate = date.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric"
                });
        
                return formattedDate;
            }
        }
        ,

        {
            field: "policyExpDate",
            headerName: "PolicyExpirationDate",
            type: "date",
            headerAlign: "center",
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            align: "center",
            flex: 4,
            valueGetter: (params) => (
                params.row.policyExpDate ? new Date(params.row.policyExpDate) : null
            ),
            renderCell: (params) => {
                if (!params.value) return ""; // Return an empty string if no date value is present
        
                const date = new Date(params.value);
        
                // Check for the placeholder date "1900-01-01"
                if (date.getFullYear() === 1900 && date.getMonth() === 0 && date.getDate() === 1) {
                    return ""; // Return an empty string if it's the placeholder date
                }
        
                // Format the date as MM/DD/YYYY
                const formattedDate = date.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric"
                });
        
                return formattedDate;
            }
        }
        ,

        {
            field: "grossPremium", 
            headerName: "GrossPremium", 
            type: "string", 
            headerAlign: "center", 
            hideSortIcons: true, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title word-wrap-1 p-0">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            renderCell: (params) => {
                let grossPremium = params.row.grossPremium;
                
                // Trim any spaces and remove the dollar sign and commas
                let cleanedValue = grossPremium.replace(/[,\$]/g, "").trim();
                
                // If the value is in parentheses, it's negative, so handle that
                if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
                    cleanedValue = "-" + cleanedValue.slice(1, -1); // Remove parentheses and add negative sign
                }
                
                // Convert to number
                const numericValue = parseFloat(cleanedValue);
                
                // If numericValue is a valid number, format it; otherwise, return the original string
                return isNaN(numericValue) ? grossPremium : `${formatPrice(numericValue.toString())}`;
            },
            align: "center", 
            flex: 3,
        },
        {
            field: "taxAmt", 
            headerName: "TaxAmount", 
            type: "string", 
            headerAlign: "center", 
            hideSortIcons: true, 
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            renderCell: (params) => {
                let taxAmt = params.row.taxAmt;
                
                // Trim any spaces and remove the dollar sign and commas
                let cleanedValue = taxAmt.replace(/[,\$]/g, "").trim();
                
                // Check if the value is in parentheses, treat it as negative if necessary
                if (cleanedValue.startsWith("(") && cleanedValue.endsWith(")")) {
                    cleanedValue = "-" + cleanedValue.slice(1, -1);  // Remove parentheses and add negative sign
                }
                
                // Convert to number
                const numericValue = parseFloat(cleanedValue);
                
                // If numericValue is a valid number, format it with a dollar sign; otherwise, return the original string
                return isNaN(numericValue) ? taxAmt : `${formatPrice(numericValue.toString())}`;
            },
            align: "center", 
            flex: 3,
        },
        {
            field: "dateFiled",
            headerName: "DateFiled",
            type: "date",
            headerAlign: "center",
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            align: "center",
            flex: 3,
            renderCell: (params) => {
                if (!params.value) return ""; // If there's no value, return an empty string
        
                const date = new Date(params.value);
        
                // Check if the date is "1900-01-01T00:00:00" or a similar representation
                if (params.value === "1900-01-01T00:00:00" || date.getFullYear() === 1900) {
                    return ""; // Return an empty string or null for the placeholder date
                }
        
                // Format the date as MM/DD/YYYY
                const formattedDate = date.toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric"
                });
        
                return formattedDate;
            }
        },
        

        {
            field: "status", headerName: "Status", type: "string", headerAlign: "center", hideSortIcons: true, renderHeader: (params) => (
                <div>
                    <span className="table-card-title" >{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), align: "center", flex: 3,
        },
    ];

    const sortModelRef = useRef<GridSortModel>([]);
    const onRowsSelectionHandler = (ids: GridSelectionModel | undefined) => {
        try {
            if (!ids || ids.length === 0) {
                setSelectedRows(null);
                return;
            }
            const selectedRows = ReportModuleCL.getData_Resp.filter((row) => ids.includes(row.customerPolicyTaxesAndFeesId ?? 0));
            setSelectedRows(selectedRows);
        } catch (err) {
            console.error('Error in onRowsSelectionHandler:', err);
            setError('Error processing row selection');
        }
    };

    function EditToolbar() {
        return (
            <React.Fragment>
                <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
                    {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
                </div>
            </React.Fragment>
        )
    }
    
    return (
        <React.Fragment>
            <div style={{ height: "100%", overflowX: "scroll" }}>
                <DataGridPro
                    getRowId={(r) => r?.customerPolicyTaxesAndFeesId}
                    rows={ReportModuleCL.getData_Resp}
                    columns={columns}
                    rowCount={ReportModuleCL.totalCount || 0}
                    rowsPerPageOptions={getRowsPerPageOptions(ReportModuleCL?.totalCount, _pageSize)}
                    pagination
                    pageSize={_pageSize}
                    paginationMode="server"
                    onPageChange={(newPage: number) => setCurrentPage(newPage + 1)}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    filterMode="server"
                    filterModel={filterModel}
                    onFilterModelChange={(newFilterModel: any) => setFilterModel(newFilterModel)}
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={(newSortModel: any) => setSortModel(newSortModel)}
                    style={{ textAlign: "center" }}

                    checkboxSelection={
                        user?.userRoles.split(",").includes("Super Admin") ||
                            user?.userRoles.split(",").includes("MISC State Reporter")
                            ? true : false
                    }
                    onSelectionModelChange={user?.userRoles.split(",").includes("Super Admin") ||
                        user?.userRoles.split(",").includes("MISC State Reporter") ? (newRowSelectionModel) => {
                            onRowsSelectionHandler(newRowSelectionModel);
                        } : undefined}
                    selectionModel={selectedRows?.map((row) => row.customerPolicyTaxesAndFeesId) as GridRowId[] || []}
                    components={{
                        Toolbar: EditToolbar,
                    }}
                    sx={{
                        border: "0px",
                        fontSize: "16px",
                        '& .MuiDataGrid-columnHeaderTitle': {
                            overflow: 'visible',
                            whiteSpace: 'normal',
                            lineHeight: 'normal',
                            wordWrap: 'break-word',
                            textOverflow: 'clip',
                        },
                        '& .MuiDataGrid-cell': {
                            overflow: 'visible',
                            whiteSpace: 'normal',
                            lineHeight: 'normal',
                            wordWrap: 'break-word',
                            textOverflow: 'clip',
                        },
                        ".custom-cell": {
                            button: {
                                padding: 0,
                                width: '100%',
                                height: '100%',
                                textAlign: 'center',
                                WebkitLineClamp: 2,
                                lineClamp: 2,
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                textOverflow: 'ellipsis',
                            }
                        },
                    }}
                    experimentalFeatures={{ newEditingApi: true }}
                />
            </div>
        </React.Fragment>
    );
};
